import { useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Check, Play } from 'lucide-react';
import { COACHING_FREQUENCY_DISCLAIMER } from '@mlm/shared/coaching';
import { FrequencyArt } from '../components/frequency-art';
import { FrequencyPlayer } from '../components/frequency-player';
import { linkButton } from '../components/panels/today';
import { Block, Chip, ErrorNote, Loading, Row, Screen } from '../components/primitives';
import { MoreWithPlan, ProLock } from '../components/pro-lock';
import { coaching, type FrequencyCategory, type FrequencyTrack } from '../lib/api';
import { durationLabel } from '../lib/format';
import { useFrequencies, useRefreshCoaching, useFeatureLocked } from '../lib/queries';
import { ImmersiveFrame, ToolFrame } from './tool-frame';

const categoryLabel = (cats: FrequencyCategory[], key: string) =>
  cats.find((c) => c.key === key)?.label ?? key;

const trackMeta = (t: FrequencyTrack, cats: FrequencyCategory[]) =>
  [t.frequencyLabel, durationLabel(t.durationSeconds), categoryLabel(cats, t.category)]
    .filter(Boolean)
    .join(' · ');

/** SC-31: the frequency library, filtered by listening intention. */
export function FrequenciesPage() {
  const locked = useFeatureLocked('frequencies');
  const library = useFrequencies();
  const [category, setCategory] = useState<string | null>(null);

  if (locked) {
    return (
      <Screen back="/align" eyebrow="Frequencies" title="Choose your listening intention">
        <ProLock feature="frequencies" />
      </Screen>
    );
  }
  if (library.isPending) return <Loading />;
  if (library.isError)
    return <ErrorNote error={library.error} retry={() => void library.refetch()} />;

  const { tracks, categories, proOnlyCount } = library.data;
  const offered = categories.filter((c) => tracks.some((t) => t.category === c.key));
  const shown = category ? tracks.filter((t) => t.category === category) : tracks;

  return (
    <Screen
      back="/align"
      eyebrow="Frequencies"
      title="Choose your listening intention"
      subtitle="Sound sessions for focus, rest and reflection."
    >
      {tracks.length === 0 ? (
        <Block title="No tracks yet">
          Frequency sessions will appear here as they are added. Check back soon.
        </Block>
      ) : (
        <>
          {offered.length > 1 && (
            <div className="flex flex-wrap gap-2 border-b border-border px-5 py-5">
              <Chip selected={category === null} onClick={() => setCategory(null)}>
                All
              </Chip>
              {offered.map((c) => (
                <Chip key={c.key} selected={category === c.key} onClick={() => setCategory(c.key)}>
                  {c.label}
                </Chip>
              ))}
            </div>
          )}
          {shown.map((t) => (
            <Row
              key={t.id}
              title={t.title}
              meta={trackMeta(t, categories)}
              to={`/align/frequencies/${t.id}`}
            />
          ))}
        </>
      )}
      <MoreWithPlan count={proOnlyCount} noun="track" />
      <p className="px-5 py-6 text-xs leading-relaxed text-muted-foreground">
        {COACHING_FREQUENCY_DISCLAIMER}
      </p>
    </Screen>
  );
}

/** One track, full screen, with the practice counted after a real minute of listening. */
export function FrequencyTrackPage() {
  const { id = '' } = useParams();
  const locked = useFeatureLocked('frequencies');
  const library = useFrequencies();

  if (locked) {
    return (
      <ToolFrame close="/align/frequencies">
        <ProLock feature="frequencies" />
      </ToolFrame>
    );
  }
  if (library.isPending) return <Loading />;
  if (library.isError)
    return <ErrorNote error={library.error} retry={() => void library.refetch()} />;
  const tracks = library.data.tracks;
  const track = tracks.find((t) => t.id === id);
  const categories = library.data.categories;
  if (!track) {
    return (
      <ToolFrame close="/align/frequencies">
        <Block
          title="This track is not available"
          action={
            <Link to="/align/frequencies" className={linkButton}>
              Choose another
            </Link>
          }
        >
          It may have been removed. Choose another session.
        </Block>
      </ToolFrame>
    );
  }
  const more = tracks.filter((t) => t.category === track.category && t.id !== track.id).slice(0, 2);
  return (
    <Listening
      key={track.id}
      track={track}
      categoryLabel={categoryLabel(categories, track.category)}
      more={more}
    />
  );
}

/** The redesign's full-screen player: the art, the transport, and the practice after a real minute. */
function Listening({
  track,
  categoryLabel,
  more,
}: {
  track: FrequencyTrack;
  categoryLabel: string;
  more: FrequencyTrack[];
}) {
  const refresh = useRefreshCoaching();
  const recorded = useRef(false);
  const [playing, setPlaying] = useState(false);
  const listened = useMutation({
    mutationFn: () => coaching.practice('frequency'),
    meta: { silentErrorCodes: ['coaching_pro_required'] },
    onSuccess: () => void refresh(),
  });
  const duration = durationLabel(track.durationSeconds);

  return (
    <ImmersiveFrame
      close="/align/frequencies"
      ground="listen"
      eyebrow={`Frequencies · ${categoryLabel}`}
    >
      <FrequencyArt
        playing={playing}
        label={track.frequencyLabel ?? duration}
        sub={track.frequencyLabel ? duration : null}
      />
      <div className="mt-[18px] text-center">
        <h1 className="font-display text-[30px] font-medium leading-[1.1] tracking-[-0.01em]">
          {track.title}
        </h1>
        {track.description && (
          <p className="mx-auto mt-2.5 max-w-[34ch] text-sm leading-[1.55] text-paper/65">
            {track.description}
          </p>
        )}
      </div>
      <FrequencyPlayer
        track={track}
        variant="immersive"
        onPlayingChange={setPlaying}
        onListened={(seconds) => {
          if (recorded.current) return;
          recorded.current = true;
          coaching.event('frequency_played', { track: track.id, seconds });
          listened.mutate();
        }}
      />
      <div className="mt-[18px] flex min-h-5 justify-center">
        {listened.isSuccess && (
          <span className="inline-flex items-center gap-2 rounded-full border border-[rgba(230,200,122,0.35)] bg-[rgba(230,200,122,0.12)] px-3 py-1.5 font-mono text-[0.62rem] uppercase tracking-[0.14em] text-[#E6C87A]">
            <Check className="h-3.5 w-3.5" strokeWidth={2} aria-hidden />
            Counted toward today's alignment
          </span>
        )}
      </div>
      <div className="mt-auto pt-[22px]">
        {more.length > 0 && (
          <>
            <p className="mb-2.5 font-mono text-[0.62rem] uppercase tracking-[0.16em] text-paper/50">
              More in {categoryLabel}
            </p>
            <div className="flex flex-col gap-1.5">
              {more.map((t) => (
                <Link
                  key={t.id}
                  to={`/align/frequencies/${t.id}`}
                  className="flex items-center gap-3 rounded-[14px] border border-paper/[0.08] bg-paper/[0.05] px-3 py-2.5 transition-colors hover:border-paper/25 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#E6C87A]"
                >
                  <span className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full border border-paper/[0.18]">
                    <Play className="ml-0.5 h-3.5 w-3.5" fill="currentColor" aria-hidden />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm leading-snug">{t.title}</span>
                    <span className="mt-0.5 block font-mono text-[0.6rem] uppercase tracking-[0.14em] text-paper/55">
                      {[t.frequencyLabel, durationLabel(t.durationSeconds)]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </span>
                </Link>
              ))}
            </div>
          </>
        )}
        <p className="mt-4 text-center text-[11.5px] leading-normal text-paper/[0.42]">
          {COACHING_FREQUENCY_DISCLAIMER} Listen at a comfortable volume.
        </p>
      </div>
    </ImmersiveFrame>
  );
}
